<template>
  <div>
    <McButton
      @click="showModal = !showModal"
      variant="secondary"
      :text="$t('global.viewSetting')"
      icon="icon-outline_layout"
    />

    <b-modal v-model="showModal" size="md" modal-class="modal-manage-view">
      <div class="wrapper p-2">
        <div class="section section-information">
          <p class="mc-general__label">{{ $t('global.viewSet.desc') }}</p>
          <div class="section-information__content p-2">
            <p>
              {{ $t('global.viewSet.click') }} <McCheckbox v-model="alwaysChecked" />
              {{ $t('global.viewSet.display') }}
            </p>
            <p>
              {{ $t('global.viewSet.click') }} <i class="icon-outline_move"></i>
              {{ $t('global.viewSet.move') }}
            </p>
          </div>
        </div>
        <div class="section section-column-manager">
          <p class="mc-general__label">{{ $t('global.viewSet.columnSettings') }}</p>
          <div class="section-column-manager__content">
            <div
              :class="`section-column-manager__header d-flex justify-content-${
                max ? 'end' : 'between'
              } p-3`"
            >
              <div class="d-flex gap-2" v-if="!max">
                <McCheckbox
                  @change="handleChangeCheckAll"
                  v-model="showAllColumn"
                  :disabled="isAnyLocked"
                />
                <span>{{ $t('global.viewSet.displayAll') }}</span>
              </div>
              <button @click="handleResetColumn">{{ $t('global.resetAsset') }}</button>
            </div>
            <draggable v-model="fieldsTemp" class="section-column-manager__draggable mb-3">
              <transition-group tag="ul">
                <li
                  class="section-column-manager__draggable-item"
                  v-for="(field, index) in fieldsTemp"
                  :key="`${index}-${field.key}`"
                  :disabled="disabledCheck(field)"
                >
                  <div class="d-flex justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                      <i class="icon-outline_move" v-if="!field.isSticky"></i>
                      <div class="__divider" v-if="!field.isSticky"></div>
                      <span class="d-flex gap-2 w-100">
                        <McCheckbox
                          v-model="field.shown"
                          :disabled="disabledCheck(field)"
                          @change="($event) => handleChangeParentCheckbox({ field, value: $event })"
                        />
                        {{ field.label }}
                      </span>
                    </div>
                    <div class="mc-hover-pointer" v-if="refreshAccordion">
                      <i
                        v-if="field.children && !field.closeChild"
                        class="toggle-list icon-outline_chevron-down"
                        @click="hideChildren(field)"
                      ></i>
                      <i
                        v-if="field.children && field.closeChild"
                        class="toggle-list icon-outline_chevron-up"
                        @click="hideChildren(field)"
                      ></i>
                    </div>
                  </div>

                  <template v-if="field.children && !field.closeChild">
                    <hr />

                    <draggable v-model="field.children">
                      <transition-group tag="ul">
                        <li
                          v-for="item in field.children"
                          :key="item.key"
                          class="section-column-manager__draggable-sub-item"
                        >
                          <div class="d-flex align-items-center gap-2">
                            <i class="icon-outline_move" v-if="!item.isSticky"></i>
                            <div class="__divider" v-if="!item.isSticky"></div>
                            <span class="d-flex gap-2 w-100">
                              <McCheckbox
                                v-model="item.shown"
                                :disabled="item.isLocked"
                                @change="
                                  ($event) => handleChangeChildCheckbox({ field, value: $event })
                                "
                              />
                              {{ item.label }}
                            </span>
                          </div>
                        </li>
                      </transition-group>
                    </draggable>
                  </template>
                </li>
              </transition-group>
            </draggable>
          </div>
        </div>
      </div>
      <template #modal-header>
        <span class="__title"> {{ $t('global.viewSetting') }} </span>
      </template>
      <template #modal-footer="{ cancel }">
        <b-row class="modal__footer mx-0">
          <b-col sm="12" md="12" class="modal__footer-btn">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              @click="cancel"
            />
            <McButton variant="primary" text="Terapkan" @click="handleAccept" />
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import draggable from 'vuedraggable'
import cloneDeep from 'lodash/cloneDeep'

export default {
  components: {
    McButton,
    McCheckbox,
    draggable,
  },
  props: {
    defaultColumnOrder: {
      type: Array,
      required: true,
    },
    currentColumnOrder: {
      type: Array,
      required: true,
    },
    max: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ['setColumn'],
  data() {
    return {
      showModal: false,
      fieldsTemp: [],
      alwaysChecked: true,
      refreshAccordion: true,
    }
  },
  computed: {
    showAllColumn: {
      get() {
        return this.fieldsTemp.every((field) => field.shown)
      },
      set(value) {
        this.fieldsTemp.forEach((field) => {
          field.shown = value
          if (field.children) {
            field.children.forEach((child) => {
              child.shown = value
            })
          }
        })
      },
    },
    isAnyLocked() {
      return this.defaultColumnOrder.every(
        (field) =>
          field.isLocked || (field.children && field.children.every((child) => child.isLocked))
      )
    },
  },
  watch: {
    showModal(newValue) {
      if (!newValue) return
      this.fieldsTemp = cloneDeep(this.currentColumnOrder)
    },
    async alwaysChecked() {
      await this.$nextTick()
      this.alwaysChecked = true
    },
  },

  methods: {
    handleAccept() {
      this.$emit('setColumn', cloneDeep(this.fieldsTemp))
      this.$emit('setColumnReport', cloneDeep(this.fieldsTemp))
      this.showModal = false
    },
    handleResetColumn() {
      this.fieldsTemp = cloneDeep(this.defaultColumnOrder)
    },
    handleChangeCheckAll(value) {
      this.fieldsTemp = this.fieldsTemp.map((field) => {
        if (field.children) {
          field.children = field.children.map((subField) => ({
            ...subField,
            shown: value,
          }))
        }

        return {
          ...field,
          shown: field.isLocked ? true : value,
        }
      })
    },
    handleChangeParentCheckbox({ field, value }) {
      if (!field.children) return

      field.children.forEach((item) => {
        item.shown = value
      })
    },
    handleChangeChildCheckbox({ field, value }) {
      if (field.shown == value) return

      let totalCount = 0

      field.children.forEach((item) => {
        if (item.shown !== value) return

        totalCount++
      })

      if (value === false && totalCount !== field.children.length) return

      field.shown = value
    },
    hideChildren(field) {
      if (field.closeChild == undefined) field.closeChild = false

      this.refreshAccordion = false

      setTimeout(() => {
        field.closeChild = !field.closeChild
        this.refreshAccordion = true
      }, 50)
    },
    disabledCheck(field) {
      if (!this.max && !field.isLocked) return false

      const checked = this.fieldsTemp.filter((x) => x.shown).map((x) => x.key)
      return (
        this.max &&
        (field.isLocked || (checked.length === this.max && !checked.includes(field.key)))
      )
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.modal-manage-view) {
  .modal-dialog {
    border-radius: 0px;
    max-height: 100vh;
    height: 100%;
    margin: 0;
    margin-left: auto;
  }

  .modal-content {
    height: 100%;
  }

  .modal-content,
  .modal-header,
  .modal-footer {
    border-radius: 0;

    .modal__footer-btn {
      display: flex;
    }
  }

  .modal-header {
    color: $white;
    justify-content: space-between;

    .__reset {
      font-size: 13px;
      text-align: right;
      cursor: pointer;
    }
  }

  .modal-body {
    overflow: hidden;
  }

  .wrapper {
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.section {
  :deep(input[type='checkbox']) {
    margin-bottom: 2px;
  }

  & > p {
    color: $teal-300;
    font-weight: 600;
  }

  &-information {
    &__content {
      border: 2px solid $blue-100;
      border-radius: 4px;
      margin-bottom: 20px;

      & > p {
        display: flex;
        gap: 5px;
        align-items: center;
        margin-bottom: 0;

        &:first-child {
          margin-bottom: 4px;
        }
      }
    }
  }

  &-column-manager {
    overflow: hidden;
    flex: 1 1 auto;

    &__content {
      box-shadow: 0px 5px 15px 0px rgba(13, 68, 145, 0.08);
      overflow-y: hidden;
      height: 100%;
    }

    &__header {
      button {
        background: transparent;
        border: none;
        color: $cyan;
        font-weight: 600;
      }
    }

    &__draggable {
      background: $gray;
      padding: 16px;
      overflow-y: auto;
      height: 100%;
      padding-bottom: 86px;

      ul {
        padding: 0;
      }

      &-item,
      &-sub-item {
        list-style-type: none;
        cursor: move;
        background: white;
        margin-bottom: 16px;
        border-radius: 5px;
        padding: 14px 12px;
        transition: 0.3s;

        .__divider {
          background-color: $teal-100;
          height: 16px;
          width: 1px;
        }

        .toggle-list {
          font-size: 20px;

          &::before {
            color: #3d69a7;
          }
        }
      }

      &-sub-item {
        list-style-type: none;
        border: 1px solid #cfdae9;
      }
    }
  }
}

.icon-outline_move {
  color: $blue-400;
  font-size: 16px;
}

.mc-hover-pointer {
  &:hover {
    cursor: pointer !important;
  }
}
</style>
